import { useState, useEffect, useRef } from 'react';
import type { VisualMapping, VisualField } from '../../../types/wizardTypes';
import { Trash2, Plus, ArrowRight } from 'lucide-react';
import styles from './MappingRow.module.css';
import SecureInput from '../../../ui/SecureInput/SecureInput';

const DEBOUNCE_MS = 250;

interface MappingRowProps {
  mapping: VisualMapping;
  availableFields: VisualField[];
  canEdit: boolean;
  canDelete?: boolean;
  onChange: (updates: Partial<VisualMapping>) => void;
  onRemove: () => void;
  onCreateField?: (fieldName: string) => void;
}

export default function MappingRow({
  mapping,
  availableFields,
  canEdit,
  canDelete = true,
  onChange,
  onRemove,
  onCreateField,
}: MappingRowProps) {
  const [localTargetPath, setLocalTargetPath] = useState(mapping.targetPath);
  const [localSourceName, setLocalSourceName] = useState(mapping.sourceFieldName);
  const targetDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sourceDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Sync external (JSON-driven) changes into local display state
  useEffect(() => {
    setLocalTargetPath(mapping.targetPath);
  }, [mapping.targetPath]);

  useEffect(() => {
    setLocalSourceName(mapping.sourceFieldName);
  }, [mapping.sourceFieldName]);

  useEffect(() => {
    return () => {
      clearTimeout(targetDebounce.current);
      clearTimeout(sourceDebounce.current);
    };
  }, []);

  const handleTargetChange = (val: string) => {
    setLocalTargetPath(val);
    clearTimeout(targetDebounce.current);
    targetDebounce.current = setTimeout(() => onChange({ targetPath: val }), DEBOUNCE_MS);
  };

  const handleSourceChange = (val: string) => {
    setLocalSourceName(val);
    clearTimeout(sourceDebounce.current);
    sourceDebounce.current = setTimeout(() => onChange({ sourceFieldName: val }), DEBOUNCE_MS);
  };

  const isUnknownField =
    availableFields.length > 0 &&
    !!mapping.sourceFieldName &&
    !availableFields.some(f => f.name === mapping.sourceFieldName);

  return (
    <div className={styles.row}>
      <div className={styles.targetSide}>
        <SecureInput
          fieldType="text"
          className={styles.pathInput}
          value={localTargetPath}
          onChange={handleTargetChange}
          placeholder="e.g. DATA.LIFNR"
          disabled={!canEdit}
        />
      </div>

      <div className={styles.arrow}>
        <ArrowRight size={16} />
      </div>

      <div className={styles.sourceSide}>
        {availableFields.length > 0 ? (
          <div className={styles.sourceInputWrap}>
            <select
              className={`${styles.sourceSelect}${isUnknownField ? ` ${styles.sourceSelectUnknown}` : ''}`}
              value={mapping.sourceFieldName}
              onChange={e => onChange({ sourceFieldName: e.target.value })}
              disabled={!canEdit}
            >
              <option value="">Select a field...</option>
              {isUnknownField && (
                <option value={mapping.sourceFieldName}>
                  {mapping.sourceFieldName}
                </option>
              )}
              {availableFields.map(field => (
                <option key={field.id} value={field.name}>
                  {field.label || field.name}
                </option>
              ))}
            </select>
            {isUnknownField && canEdit && onCreateField && (
              <button
                type="button"
                className={styles.createFieldChip}
                onClick={() => onCreateField(mapping.sourceFieldName)}
              >
                <Plus size={10} />
                Create input field &quot;{mapping.sourceFieldName}&quot;
              </button>
            )}
          </div>
        ) : (
          <SecureInput
            fieldType="text"
            className={styles.pathInput}
            value={localSourceName}
            onChange={handleSourceChange}
            placeholder="e.g. fieldName"
            disabled={!canEdit}
          />
        )}
      </div>

      {canEdit && canDelete && (
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={onRemove}
          aria-label="Remove mapping"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}
